import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Mail, { MailsByDomiciliations, UserMailToForwardType } from 'globals/models/Mail'
import MailDetails from 'pages/ClientDetails/components/MailDetails'
import { useLazyQuery, useMutation } from '@apollo/client'
import { MAILS_TO_FORWARD, MailsToForwardResult, MailsToForwardVariables } from 'graphql/mails/queries'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import Loader, { LoaderContainer } from 'components/Loader'
import {
  MARK_MAILS_AS_REDIRECTED,
  MarkMailsAsRedirectedResult,
  MarkMailsAsRedirectedVariables
} from 'pages/Mails/mutations'
import { displayErrors } from 'globals/graphql'
import { popupGraphQLError } from 'components/GraphQLError'
import popupSuccess from 'globals/popupSuccess'
import SelectList from '../components/SelectList'
import Topbar from '../components/Topbar'
import { ActionBar } from '../components/styles'
import SelectedMailsButtons from 'components/SelectedMailsButtons'
import { SearchBar } from 'components'
import { isMobile } from 'react-device-detect'

interface IProps {
  mailType: UserMailToForwardType
}

const ForwardingMails = (props: IProps) => {
  const pageLength = 5

  const { t } = useTranslation()
  const { mailType } = props
  const [mailDetails, setMailDetails] = useState<Mail | null>(null)
  const [showMailDetails, setShowMailDetails] = useState(false)
  const [mailsToForward, setMailsToForward] = useState<MailsByDomiciliations[]>([])
  const [noMoreMails, setNoMoreMails] = useState(false)
  const [selectedMailsIds, setSelectedMailsIds] = useState<React.Key[]>([])
  const [searchParams, setSearchParams] = useState({
    searchTerm: '',
    mailType: mailType,
    currentPage: 1,
    pageLength: pageLength
  })

  const [markMailsAsRedirected] = useMutation<MarkMailsAsRedirectedResult, MarkMailsAsRedirectedVariables>(
    MARK_MAILS_AS_REDIRECTED,
    displayErrors
  )

  const onMailsRetrieved = (mailsToForwardResult: MailsToForwardResult) => {
    displayErrors.onCompleted(mailsToForwardResult)

    if (mailsToForwardResult) {
      const retrieved = mailsToForwardResult?.userMailsToForward?.userMailsToForwardByDomiciliation || []
      const currentPage = mailsToForwardResult?.userMailsToForward?.pagination?.currentPage || 0
      const totalPages = mailsToForwardResult?.userMailsToForward?.pagination?.totalPages || 1

      if (currentPage == 1) {
        setMailsToForward(retrieved)
      } else {
        setMailsToForward((mails) => mails.concat(retrieved))
      }

      if (currentPage >= totalPages) {
        setNoMoreMails(true)
      }

      popupGraphQLError(mailsToForwardResult)
    }
  }

  const [fetchMailsToForward, { loading, data }] = useLazyQuery<MailsToForwardResult, MailsToForwardVariables>(
    MAILS_TO_FORWARD,
    {
      ...displayErrors,
      onCompleted: onMailsRetrieved,
      fetchPolicy: 'no-cache'
    }
  )

  const fetchInitialMails = (search: string) => {
    const variables = { search: search, page: 1, mailType: mailType, pageLength: searchParams.pageLength }
    fetchMailsToForward({ variables })
    setSearchParams({ ...searchParams, searchTerm: search, currentPage: 1 })
  }

  const fetchMoreMails = () => {
    fetchMailsToForward({
      variables: {
        search: searchParams.searchTerm,
        mailType: searchParams.mailType,
        page: searchParams.currentPage,
        pageLength: searchParams.pageLength
      }
    })
    setSearchParams({ ...searchParams, currentPage: searchParams.currentPage + 1 })
  }

  const [sentryRef] = useInfiniteScroll({
    loading: loading,
    hasNextPage:
      !noMoreMails &&
      ((data?.userMailsToForward?.userMailsToForwardByDomiciliation || []).length > 0 || mailsToForward.length <= 0),
    delayInMs: 1000,
    rootMargin: '0px 0px 400px 0px',
    onLoadMore: fetchMoreMails
  })

  const hideMails = (ids: React.Key[]) => {
    setSelectedMailsIds([])
    setMailsToForward((mailsToForward) =>
      mailsToForward.map((mails) => {
        const userMails = mails.userMails.filter((m) => !ids.includes(m.id))
        return { ...mails, userMails: userMails, userMailsCount: userMails.length }
      })
    )
  }

  const onConfirm = (ids: React.Key[]) => {
    markMailsAsRedirected({ variables: { userMailIds: ids.map(Number) } }).then(() => {
      hideMails(ids)
      popupSuccess('Courrier(s) marqué(s) comme réexpédié(s)')
    })
  }

  const onMarkAsTracked = () => {
    if (!mailDetails) return
    markMailsAsRedirected({ variables: { userMailIds: [mailDetails.id] } }).then(() => {
      hideMails([mailDetails.id])
      popupSuccess('Numéro de suivi ajouté')
    })
  }

  return (
    <>
      <Topbar totalCount={data?.userMailsToForward?.pagination?.totalItemsCount} />
      <ActionBar>
        {mailType === 'parcels' ? (
          <div />
        ) : (
          <SelectedMailsButtons
            selectedMailsNumber={selectedMailsIds.length}
            confirmText={t('mails.forwardings.confirm')}
            onConfirm={() => onConfirm(selectedMailsIds)}
            onCancel={() => setSelectedMailsIds([])}
          />
        )}
        {!isMobile && (
          <SearchBar
            onSearch={() => null}
            onChange={(value: string) => fetchInitialMails(value)}
            value={searchParams.searchTerm}
            hideFilters
            placeholder={'Rechercher ID'}
            marginBottom={false}
            height={'31px'}
            backgroundColor={'#F0F1F3'}
          />
        )}
      </ActionBar>
      <SelectList
        userMailsByDomiciliation={mailsToForward}
        totalCount={data?.userMailsToForward?.pagination?.totalItemsCount || 1}
        onMailClicked={(mail: Mail) => {
          setMailDetails(mail)
          setShowMailDetails(true)
        }}
        scrollLoader={
          !noMoreMails && (
            <div>
              <LoaderContainer ref={sentryRef}>
                <Loader />
              </LoaderContainer>
            </div>
          )
        }
        alwaysHideCheckboxes={mailType === 'parcels'}
        defaultChecked
        quickAction={mailType === 'parcels' ? 'forward' : ''}
        quickActionCallback={hideMails}
        lineStyle={'agenda'}
        selectedMailsIds={selectedMailsIds}
        setSelectedMailsIds={setSelectedMailsIds}
      />
      <MailDetails
        mail={mailDetails}
        visible={showMailDetails}
        onClose={() => {
          setShowMailDetails(false)
          setMailDetails(null)
        }}
        disableNpai={true}
        markAsTracked={onMarkAsTracked}
      />
    </>
  )
}

export default ForwardingMails
